import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useAuth } from '../hooks/useAuth';

const DEFAULT_INPUT = 'D\n#';

type BetResponse = {
  number: string;
  bigAmount: number;
  smallAmount: number;
  fourAAmount: number;
  aAmount: number;
  abcAmount: number;
  fiveDAmount: number;
  sixDAmount: number;
  operatorName?: string;
  drawDate?: string;
  status?: string;
};

const operatorNameToCode: Record<string, string> = {
  'Magnum 4D': 'M',
  'Damacai 4D': 'P',
  'Sports Toto': 'T',
  'Singapore 4D': 'S',
  'Cash Sweep': 'B',
  'Sabah 88': 'K',
  'STC 4D': 'W',
  'Grand Dragon': 'H',
  '9 Lotto': 'L'
};

const operatorOrder = ['M', 'P', 'T', 'S', 'B', 'K', 'W', 'H', 'L'];

const formatNumber = (amount: number) => {
  if (amount === undefined || amount === null) return '';
  return Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
};

const factorial = (n: number): number => {
  if (n <= 1) return 1;
  let result = 1;
  for (let i = 2; i <= n; i++) {
    result *= i;
  }
  return result;
};

const calculatePermutationCount = (digits: string): number => {
  if (!digits) return 1;
  const counts: Record<string, number> = {};
  digits.split('').forEach((digit) => {
    counts[digit] = (counts[digit] || 0) + 1;
  });

  let denominator = 1;
  Object.values(counts).forEach((count) => {
    denominator *= factorial(count);
  });

  return factorial(digits.length) / denominator;
};

const getPatternMultiplier = (pattern: string): number => {
  if (!pattern) return 1;
  const trimmed = pattern.trim();

  if (trimmed.includes('-')) {
    const [startStr, endStr] = trimmed.split('-');
    const start = Number(startStr);
    const end = Number(endStr);
    if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
      return Math.abs(end - start) + 1;
    }
  }

  if (/^\*{3}\d{3}$/.test(trimmed) ||
      /^\d\*{3}\d{2}$/.test(trimmed) ||
      /^\d{2}\*{3}\d$/.test(trimmed) ||
      /^\d{3}\*{3}$/.test(trimmed)) {
    return 10;
  }

  const starCount = (trimmed.match(/\*/g) || []).length;
  if (starCount === 1) {
    const digitsOnly = trimmed.replace(/[^0-9]/g, '');
    if (digitsOnly.length === 4) {
      const starIndex = trimmed.indexOf('*');
      const digitsBeforeStar = trimmed
        .slice(0, starIndex)
        .replace(/[^0-9]/g, '')
        .length;
      const digitsAfterStar = digitsOnly.length - digitsBeforeStar;

      if (digitsBeforeStar === 1 && digitsAfterStar === 3) {
        const variablePart = digitsOnly.slice(1);
        return calculatePermutationCount(variablePart);
      }

      if (digitsBeforeStar === 3 && digitsAfterStar === 1) {
        const variablePart = digitsOnly.slice(0, 3);
        return calculatePermutationCount(variablePart);
      }
    }
  }

  return 1;
};

const formatDateDisplay = (input?: string) => {
  if (!input) return 'Unknown Date';
  // If input is already in YYYY-MM-DD format, parse it directly
  if (/^\d{4}-\d{2}-\d{2}/.test(input)) {
    const [year, month, day] = input.split('-');
    return `${day}/${month}/${year}`;
  }
  // Otherwise, parse as date and use UTC components to avoid timezone issues
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return input;
  }
  // Use UTC methods to avoid timezone conversion
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const formatReceipt = (data: unknown): string => {
  if (!Array.isArray(data) || data.length === 0) {
    return '';
  }

  const groups = new Map<string, {
    operators: Set<string>;
    numbers: Map<string, BetResponse>;
  }>();

  (data as BetResponse[]).forEach((bet) => {
    const raw = bet as any;
    const normalized: BetResponse = {
      number: raw.number ?? raw.Number ?? '',
      bigAmount: raw.bigAmount ?? raw.BigAmount ?? 0,
      smallAmount: raw.smallAmount ?? raw.SmallAmount ?? 0,
      fourAAmount: raw.fourAAmount ?? raw.FourAAmount ?? 0,
      aAmount: raw.aAmount ?? raw.AAmount ?? 0,
      abcAmount: raw.abcAmount ?? raw.AbcAmount ?? 0,
      fiveDAmount: raw.fiveDAmount ?? raw.FiveDAmount ?? 0,
      sixDAmount: raw.sixDAmount ?? raw.SixDAmount ?? 0,
      operatorName: raw.operatorName ?? raw.OperatorName ?? undefined,
      drawDate: raw.drawDate ?? raw.DrawDate ?? undefined,
      status: raw.status ?? raw.Status ?? undefined
    };

    if (!normalized.number) {
      return;
    }

    // Extract date string directly to avoid timezone conversion issues
    // If drawDate is already a date string (YYYY-MM-DD), use it directly
    // Otherwise parse it and extract the date portion without timezone conversion
    let drawKey = 'unknown';
    if (normalized.drawDate) {
      if (typeof normalized.drawDate === 'string') {
        // If it's already a date string in YYYY-MM-DD format, use it directly
        if (/^\d{4}-\d{2}-\d{2}/.test(normalized.drawDate)) {
          drawKey = normalized.drawDate.slice(0, 10);
        } else {
          // Parse the date and extract date portion without timezone conversion
          const date = new Date(normalized.drawDate);
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          drawKey = `${year}-${month}-${day}`;
        }
      } else {
        // If it's a Date object, extract UTC date components
        const date = new Date(normalized.drawDate);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        drawKey = `${year}-${month}-${day}`;
      }
    }
    if (!groups.has(drawKey)) {
      groups.set(drawKey, { operators: new Set(), numbers: new Map() });
    }

    const group = groups.get(drawKey)!;

    const operatorCode = normalized.operatorName ? (operatorNameToCode[normalized.operatorName] || normalized.operatorName.charAt(0).toUpperCase()) : '?';
    group.operators.add(operatorCode);

    if (!group.numbers.has(normalized.number)) {
      group.numbers.set(normalized.number, normalized);
    }
  });

  const groupEntries = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  const blocks: string[] = [];

  groupEntries.forEach(([drawKey, group]) => {
    const displayDate = formatDateDisplay(drawKey);
    const operatorsSorted = Array.from(group.operators).sort((a, b) => {
      const indexA = operatorOrder.indexOf(a);
      const indexB = operatorOrder.indexOf(b);
      const safeA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const safeB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      return safeA - safeB;
    });

    const numberLines = Array.from(group.numbers.values())
      .sort((a, b) => a.number.localeCompare(b.number))
      .map((bet) => {
        const multiplier = getPatternMultiplier(bet.number);
        let numberLabel = bet.number;

        if (bet.number.includes('***')) {
          numberLabel = bet.number.replace('***', 'x');
        } else if (multiplier > 1) {
          numberLabel = `${bet.number} (x${multiplier})`;
        }

        // Check if this is a DRAW_NOT_OPEN bet (sold out)
        const isDrawNotOpen = bet.status === 'DRAW_NOT_OPEN' || 
                             (bet.bigAmount === 0 && bet.smallAmount === 0 && bet.fourAAmount === 0 && 
                              bet.aAmount === 0 && bet.abcAmount === 0 && bet.fiveDAmount === 0 && 
                              bet.sixDAmount === 0 && bet.status !== undefined);

        const pieces: string[] = [];
        if (bet.bigAmount > 0) pieces.push(`${formatNumber(bet.bigAmount)}B`);
        if (bet.smallAmount > 0) pieces.push(`${formatNumber(bet.smallAmount)}S`);
        if (bet.fourAAmount > 0) pieces.push(`${formatNumber(bet.fourAAmount)}4A`);
        if (bet.aAmount > 0) pieces.push(`${formatNumber(bet.aAmount)}A`);
        if (bet.abcAmount > 0) pieces.push(`${formatNumber(bet.abcAmount)}ABC`);
        if (bet.fiveDAmount > 0) pieces.push(`${formatNumber(bet.fiveDAmount)}5D`);
        if (bet.sixDAmount > 0) pieces.push(`${formatNumber(bet.sixDAmount)}6D`);

        const amounts = pieces.join(' ');
        const lineContent = amounts ? `${numberLabel} ${amounts}` : numberLabel;
        
        // Append (S.O) for sold out bets
        return isDrawNotOpen ? `${lineContent} (S.O)` : lineContent;
      });

    const blockLines = ['D', displayDate, operatorsSorted.join('') || '-'];
    blockLines.push(...numberLines);
    blocks.push(blockLines.join('\n'));
  });

  return blocks.join('\n\n');
};

const MobileBetPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [rawInput, setRawInput] = useState<string>(DEFAULT_INPUT);
  const [receipt, setReceipt] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [availableOperators, setAvailableOperators] = useState<Array<{ Id?: number; id?: number; Code?: string; code?: string; Name?: string; name?: string }>>([]);
  const [loadingOperators, setLoadingOperators] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!rawInput.trim()) {
      enqueueSnackbar('Please enter at least one line before submitting.', { variant: 'warning' });
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await axios.post('/bet/place-mobile-text', {
        rawInput
      });

      // Extract bets array from response (PlaceMultipleBets returns { bets: [...], summary: {...} })
      const betsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.bets || []);
      
      const receiptText = formatReceipt(betsData);
      setReceipt(receiptText);
      enqueueSnackbar('Mobile bet submitted successfully.', { variant: 'success' });
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      const apiErrors = error?.response?.data?.errors;
      const combinedMessage = [apiMessage, Array.isArray(apiErrors) ? apiErrors.join('\n') : undefined]
        .filter(Boolean)
        .join('\n');

      const fallbackMessage = error?.message || 'Failed to submit mobile bet.';
      const finalMessage = combinedMessage || fallbackMessage;

      setErrorMessage(finalMessage);
      enqueueSnackbar(finalMessage, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setRawInput(DEFAULT_INPUT);
    setReceipt('');
    setErrorMessage('');
  };

  // Fetch available operators for today (or tomorrow if after 8 PM)
  // Backend automatically handles the 8 PM logic, so we don't need to pass a date
  const fetchAvailableOperators = useCallback(async () => {
    setLoadingOperators(true);
    try {
      // Don't pass date parameter - backend will automatically use tomorrow if after 8 PM
      const response = await axios.get(`/operator/available`);
      console.log('API Response:', response.data); // Debug log
      if (response.data?.availableOperators) {
        console.log('Setting operators:', response.data.availableOperators); // Debug log
        setAvailableOperators(response.data.availableOperators);
      } else {
        console.warn('No availableOperators in response'); // Debug log
        setAvailableOperators([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch available operators:', error);
      console.error('Error details:', error.response?.data); // Debug log
      // Don't show error to user, just use empty array
      setAvailableOperators([]);
    } finally {
      setLoadingOperators(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableOperators();
  }, [fetchAvailableOperators]);

  // Get operator codes for available operators, sorted by operator order
  const getAvailableOperatorCodes = () => {
    // Map operator names/codes to display codes
    const codeMap: Record<string, string> = {
      ...operatorNameToCode,
      'GRAND_DRAGON': 'H',
      'PERDANA': 'L',
      'Grand Dragon': 'H',
      '9 Lotto': 'L'
    };
    
    console.log('Available operators:', availableOperators); // Debug log
    
    const codes = availableOperators
      .map(op => {
        // Handle both camelCase and PascalCase property names from API
        const operatorName = op.Name || (op as any).name;
        const operatorCode = op.Code || (op as any).code;
        
        // Try operator name first, then code, then fallback
        let code = codeMap[operatorName] || codeMap[operatorCode];
        
        // If no mapping found, use first character of code (with null check)
        if (!code && operatorCode) {
          code = operatorCode.charAt(0).toUpperCase();
        } else if (!code) {
          // If no code at all, skip this operator
          console.warn('Operator missing name and code:', op);
          return null;
        }
        
        console.log(`Operator: ${operatorName} (${operatorCode}) -> Code: ${code}`); // Debug log
        return code;
      })
      .filter((code): code is string => {
        // Filter out null values and check if code is in operatorOrder
        if (!code) return false;
        const isValid = operatorOrder.includes(code);
        if (!isValid) {
          console.log(`Filtered out code: ${code} (not in operatorOrder)`); // Debug log
        }
        return isValid;
      })
      .sort((a, b) => {
        const indexA = operatorOrder.indexOf(a);
        const indexB = operatorOrder.indexOf(b);
        return indexA - indexB;
      });
    
    console.log('Final codes:', codes); // Debug log
    return codes;
  };

  // Generate operator mapping display (e.g., "#1 is H, #2 is L")
  const getOperatorMapping = () => {
    const codes = getAvailableOperatorCodes();
    if (codes.length === 0) return 'No operators available';
    
    return codes.map((code, index) => `#${index + 1} is ${code}`).join(', ');
  };

  // Calculate draw type display - this will be recalculated on every render
  const availableCodes = React.useMemo(() => getAvailableOperatorCodes(), [availableOperators]);
  const drawTypeDisplay = React.useMemo(() => {
    const codes = availableCodes;
    const result = codes.length > 0 ? codes.join('-') : '-';
    console.log('Draw type display:', result, 'from codes:', codes); // Debug log
    return result;
  }, [availableCodes]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CasinoIcon />
          Mobile Bet
        </Typography>
        <Chip
          label={`Balance: $${user?.credits?.toFixed(2) ?? '0.00'}`}
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* How to Buy Guide */}
      <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoIcon />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              How to Buy
            </Typography>
          </Stack>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Bet Format</TableCell>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Box / IBox</TableCell>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Draw Date / Day</TableCell>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Draw Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>B-S-4A-C-A</TableCell>
                <TableCell>* / **</TableCell>
                <TableCell># / ##</TableCell>
                <TableCell>
                  {loadingOperators ? (
                    <CircularProgress size={16} />
                  ) : availableOperators.length === 0 ? (
                    <Typography component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      Loading...
                    </Typography>
                  ) : (
                    <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {drawTypeDisplay || '-'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {!loadingOperators && availableCodes.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Operator Mapping: {getOperatorMapping()}
            </Typography>
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <TextSnippetIcon color="primary" />
              <Typography variant="h6">Entry</Typography>
            </Stack>

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </Alert>
            )}

            <Box
              component="textarea"
              value={rawInput}
              onChange={(event) => setRawInput(event.target.value)}
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              sx={{
                flexGrow: 1,
                fontFamily: 'monospace',
                fontSize: 16,
                lineHeight: 1.4,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
                resize: 'none',
                outline: 'none',
                p: 1.5,
                minHeight: 320
              }}
            />

            <Stack direction="row" spacing={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {isSubmitting ? 'Submitting…' : 'Bet'}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                Reset
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <ReceiptLongIcon color="primary" />
              <Typography variant="h6">Receipt</Typography>
            </Stack>

            <Box
              component="pre"
              sx={{
                flexGrow: 1,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: 'background.paper',
                p: 1.5,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: 14,
                whiteSpace: 'pre-wrap'
              }}
            >
              {receipt || 'Submit a bet to view the receipt output here.'}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MobileBetPage;

